/**
 * Firebase initialization module
 * Handles the setup and configuration of Firebase services
 * @module utils/firebase
 */
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';

/**
 * Initializes Firebase with configuration from JSON file
 * @async
 * @returns {Promise<{app: Object, analytics: Object}>} - Firebase app and analytics instances
 * @throws {Error} - If configuration can't be loaded or is invalid
 */
export async function initializeFirebase() {
    // Fetch configuration from JSON file as originally implemented
    try {
        const response = await fetch('/json/config.json');
        if (!response.ok) {
            throw new Error(`Failed to fetch config: ${response.status} ${response.statusText}`);
        }
        
        const configData = await response.json();
        
        if (!configData || !configData.firebase) {
            throw new Error('Invalid config.json format: missing firebase configuration');
        }
        
        // Ensure all required Firebase config values are present
        const firebaseConfig = configData.firebase;
        
        // Debug log to trace the issue
        console.log('Firebase config loaded:', JSON.stringify({
            apiKey: firebaseConfig.apiKey ? '✓' : '✗',
            authDomain: firebaseConfig.authDomain ? '✓' : '✗',
            projectId: firebaseConfig.projectId ? '✓' : '✗',
            storageBucket: firebaseConfig.storageBucket ? '✓' : '✗',
            messagingSenderId: firebaseConfig.messagingSenderId ? '✓' : '✗',
            appId: firebaseConfig.appId ? '✓' : '✗',
            measurementId: firebaseConfig.measurementId ? '✓' : '✗'
        }));
        
        const app = initializeApp(firebaseConfig);
        const analytics = getAnalytics(app);
        
        console.log('Firebase initialized successfully with projectId:', firebaseConfig.projectId);
        return { app, analytics };
    } catch (error) {
        console.error('Error initializing Firebase:', error);
        
        // Instead of hardcoding a fallback with sensitive keys, just notify that Firebase is disabled
        console.warn('Firebase configuration unavailable - Firebase services will be disabled');
        
        // Return null values to indicate Firebase is not available
        return { app: null, analytics: null };
        
        // No need to try initializing with incomplete or insecure config
        // The application will continue without Firebase functionality
    }
}

// Firebase initialization is now exported as a named export
